import type { Katalog } from "@gui-creator/gemeinsam";
import { useId, useState } from "react";
import { useStruktur } from "../api/abfragen.ts";

const EIGENE = "__eigene__";

/** Aktion aus dem Katalog samt passendem Argument-Editor; unbekannte Aktionen bleiben als eigene stehen. */
export function AktionWahl({
  katalog,
  pluginId,
  aktion,
  wert,
  onAendern,
  deaktiviert,
}: {
  katalog: Katalog;
  pluginId: string;
  aktion: string | undefined;
  wert: string | undefined;
  onAendern: (aktion: string | null, wert: string | null) => void;
  deaktiviert?: boolean;
}) {
  const id = useId();
  const struktur = useStruktur();
  const bekannt = katalog.aktionen.find((a) => a.schluessel === aktion);
  const [eigeneOffen, setEigeneOffen] = useState(!!aktion && !bekannt);
  const auswahl = !aktion ? "" : bekannt ? aktion : EIGENE;
  const uis = struktur.data?.plugins.find((p) => p.id === pluginId)?.workspaces.flatMap((w) => w.uis.map((u) => ({ ...u, workspace: w.name }))) ?? [];

  return (
    <div className="feldgruppe">
      <div className="feld">
        <label htmlFor={`${id}-a`}>Aktion</label>
        <select
          id={`${id}-a`}
          className="eingabe"
          value={eigeneOffen ? EIGENE : auswahl}
          disabled={deaktiviert}
          onChange={(e) => {
            const v = e.target.value;
            if (v === EIGENE) return setEigeneOffen(true);
            setEigeneOffen(false);
            onAendern(v || null, null);
          }}
        >
          <option value="">Keine</option>
          {katalog.aktionen.map((a) => (
            <option key={a.schluessel} value={a.schluessel}>
              {a.name}
            </option>
          ))}
          <option value={EIGENE}>Eigene Aktion …</option>
        </select>
        {bekannt?.beschreibung && <span className="feld-hinweis">{bekannt.beschreibung}</span>}
      </div>
      {eigeneOffen && (
        <div className="feld">
          <label htmlFor={`${id}-e`}>Aktionsschlüssel</label>
          <input id={`${id}-e`} className="eingabe mono" defaultValue={bekannt ? "" : (aktion ?? "")} placeholder="z. B. shop.oeffnen" disabled={deaktiviert} onBlur={(e) => e.target.value.trim() && onAendern(e.target.value.trim(), wert ?? null)} />
          <span className="feld-hinweis">Nicht im Katalog. Die Prüfung warnt, bis der Katalog sie kennt.</span>
        </div>
      )}
      {bekannt && bekannt.argument !== "keins" && (
        <div className="feld">
          <label htmlFor={`${id}-w`}>{bekannt.argument === "ui" ? "Öffnet UI" : "Argument"}</label>
          {bekannt.argument === "ui" ? (
            <select id={`${id}-w`} className="eingabe" value={wert ?? ""} disabled={deaktiviert} onChange={(e) => onAendern(aktion!, e.target.value || null)}>
              <option value="">Bitte wählen</option>
              {uis.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.workspace} › {u.name ?? u.schluessel}
                </option>
              ))}
              {wert && !uis.some((u) => u.id === wert) && <option value={wert}>Gelöschte UI</option>}
            </select>
          ) : typeof bekannt.argument === "object" ? (
            <select id={`${id}-w`} className="eingabe" value={wert ?? ""} disabled={deaktiviert} onChange={(e) => onAendern(aktion!, e.target.value || null)}>
              <option value="">Bitte wählen</option>
              {bekannt.argument.werte.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          ) : (
            <input id={`${id}-w`} key={wert} className="eingabe mono" defaultValue={wert ?? ""} disabled={deaktiviert} placeholder="Wert oder {platzhalter}" onBlur={(e) => onAendern(aktion!, e.target.value || null)} />
          )}
        </div>
      )}
    </div>
  );
}
